<template>
  <article
    class="alert-card"
    :class="['sev-' + alert.severity, { expanded }]"
    :data-test="`alert-${alert.rule}`"
  >
    <div class="alert-head">
      <span :class="['sev-dot', alert.severity]" :aria-label="`severity ${alert.severity}`" />
      <div class="alert-title-block">
        <h3 class="alert-title">{{ alert.title }}</h3>
        <p class="alert-body">{{ alert.body }}</p>
      </div>
    </div>

    <div class="alert-foot">
      <span class="alert-action mono">{{ alert.actionLabel }}</span>
      <button
        type="button"
        class="explain-btn"
        :aria-expanded="expanded"
        :data-test="`alert-${alert.rule}-explain`"
        @click="onToggle"
      >
        {{ expanded ? "Hide" : "Explain" }}
      </button>
    </div>

    <section v-if="expanded" class="explain-pane" data-test="alert-explain-pane">
      <p v-if="isLoading" class="explain-loading">Generating explanation…</p>
      <pre v-else-if="explanation" class="explain-text">{{ explanation }}</pre>
      <p v-else-if="errorText" class="explain-error">{{ errorText }}</p>
    </section>
  </article>
</template>

<script setup lang="ts">
import { ref } from "vue";
import type { GovernanceAlert } from "@/lib/governanceAlerts";
import { useExplainAlert } from "@/composables/useExplainAlert";

const props = defineProps<{ alert: GovernanceAlert }>();

const { explain, cached, error } = useExplainAlert();
const expanded = ref(false);
const isLoading = ref(false);
const explanation = ref<string | null>(null);
const errorText = ref<string | null>(null);

async function onToggle() {
  if (expanded.value) {
    expanded.value = false;
    return;
  }
  expanded.value = true;
  const hit = cached(props.alert.id);
  if (hit) {
    explanation.value = hit;
    return;
  }
  isLoading.value = true;
  errorText.value = null;
  try {
    explanation.value = await explain(props.alert);
  } catch (e) {
    errorText.value = e instanceof Error ? e.message : String(e);
    explanation.value = null;
  } finally {
    isLoading.value = false;
  }
  if (error.value && !explanation.value) errorText.value = error.value;
}
</script>

<style scoped>
.alert-card {
  background: var(--bg-card, #fff);
  border: 1px solid var(--border, #e5e7eb);
  border-left-width: 3px;
  border-radius: 6px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.alert-card.sev-high { border-left-color: #dc2626; }
.alert-card.sev-medium { border-left-color: #d97706; }
.alert-card.sev-low { border-left-color: #2563eb; }

.alert-head { display: flex; gap: 10px; align-items: flex-start; }
.sev-dot {
  width: 8px; height: 8px; border-radius: 50%;
  margin-top: 6px; flex-shrink: 0;
}
.sev-dot.high { background: #dc2626; }
.sev-dot.medium { background: #d97706; }
.sev-dot.low { background: #2563eb; }

.alert-title-block { min-width: 0; }
.alert-title { font-size: 13px; font-weight: 600; margin: 0; line-height: 1.3; }
.alert-body { font-size: 12px; color: var(--fg-muted, #6b7280); margin: 4px 0 0; line-height: 1.45; }

.alert-foot {
  display: flex; align-items: center; justify-content: space-between; gap: 8px;
  border-top: 1px dashed var(--border, #e5e7eb); padding-top: 8px;
}
.alert-action {
  font-size: 11px; color: var(--fg-subtle, #9ca3af); text-transform: uppercase; letter-spacing: 0.04em;
}
.mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }

.explain-btn {
  background: transparent;
  border: 1px solid var(--border, #e5e7eb);
  color: var(--accent, #2563eb);
  font-size: 11px;
  padding: 3px 10px;
  border-radius: 999px;
  cursor: pointer;
}
.explain-btn:hover { background: var(--bg-elev, #f9fafb); }

.explain-pane {
  border-top: 1px dashed var(--border, #e5e7eb);
  padding-top: 8px;
  font-size: 12px;
  color: var(--fg-muted, #6b7280);
}
.explain-loading { margin: 0; font-style: italic; }
.explain-text { margin: 0; white-space: pre-wrap; font-family: inherit; line-height: 1.5; }
.explain-error { margin: 0; color: #991b1b; }
</style>
